import React from 'react'
import { createRoot, Root } from 'react-dom/client'
import { BaseNotifier } from './baseNotifier'
import Notification from '../elementals/notification'
import { Toaster, toast } from 'sonner'
import { NOTIFICATION_ROLES } from './constants'

const MAX_VISIBLE_TOASTS = 5

type NotificationRole =
  (typeof NOTIFICATION_ROLES)[keyof typeof NOTIFICATION_ROLES]

const defaultIconName: Record<NotificationRole, string> = {
  info: 'info-circle',
  caution: 'exclamation-triangle',
  error: 'exclamation-circle',
  success: 'check-circle',
}

export interface ToastOptions {
  /** The visual style/color of the toast */
  role?: NotificationRole
  /** The main message content */
  message?: React.ReactNode
  /** The title/header of the toast */
  title?: string
  /** Whether the toast should automatically dismiss after duration */
  autoDismiss?: boolean
  /** Whether the user can manually dismiss the toast */
  dismissible?: boolean
  /** Duration in milliseconds before auto-dismiss (default: 5000) */
  duration?: number
  /** Whether to show an icon */
  showIcon?: boolean
  /** Custom icon name (overrides default based on role) */
  iconName?: string
  /** Pass an error object to automatically extract message and set error role */
  error?:
    | Error
    | {
        message?: string
        statusText?: string
        body?: { title?: string; message?: string }
      }
}

export class ToastNotifier extends BaseNotifier {
  private _containerDomNode: HTMLDivElement | null
  private _root: Root | null = null

  constructor() {
    super()
    this._containerDomNode = null
  }

  initialize(): void {
    // attach notification to body, open it via ref
    this._containerDomNode = document.createElement('div')
    document.body.appendChild(this._containerDomNode)
    this._root = createRoot(this._containerDomNode)
    this._root.render(
      <Toaster
        visibleToasts={MAX_VISIBLE_TOASTS}
        toastOptions={{ unstyled: true }}
      />
    )
  }

  open(initialOptions: ToastOptions): string | number {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const options = super.prepareOptions(initialOptions)
    const {
      role,
      message,
      title,
      autoDismiss,
      dismissible,
      duration,
      showIcon,
      iconName,
    } = options as ToastOptions & { role: NotificationRole }

    /**
     * For now we won't do much with the returned toastId, other than use it to dismiss the toast - and use it
     * for unit testing.  In future, we may want to get more use out of it, TBD.
     */
    const toastId = toast(
      <Notification
        isVisible
        iconName={iconName || (showIcon ? defaultIconName[role] : null)}
        iconStyle='light'
        color={role}
        message={message}
        title={title}
        fluid
        onCloseButton={dismissible}
        onClose={
          dismissible
            ? () => {
                toast.dismiss(toastId)
              }
            : undefined
        }
      />,
      {
        description: message,
        duration: autoDismiss ? duration : Infinity,
      }
    )
    return toastId
  }

  close(): void {
    // not implemented for now
  }

  closeAll(): void {
    toast.dismiss()
  }

  destroy(): void {
    // unmount and remove the container from the body
    if (this._containerDomNode) {
      this._root?.unmount()
      this._root = null
      document.body.removeChild(this._containerDomNode)
    }
    super.destroy()
  }
}
